import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Request from "common/Request";
import { parseNames } from "../api";
import { atBqnTarget } from "common/api";
import BqnContext from "common/BqnContext";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import AddressInput from "./AddressInput";
import SwitchInput from "common/SwitchInput";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import SelectAutocomplete from "common/SelectAutocomplete";

const _linesOptions = [
  { label: "100", value: 100 },
  { label: "1000", value: 1000 },
  { label: "10000", value: 10000 },
  { label: "100000", value: 100000 },
  { label: "1000000", value: 1000000 },
];
const _directionOptions = [
  { label: "Downlink", value: "downlink" },
  { label: "Uplink", value: "uplink" },
];
const nameAsOption = (name) => ({ label: name, value: name });
// The policies and the groups of the BQN selected in the header fill the
// filters, or the ones of the whole cluster.
const requestRatePolicies = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show policy rate", mode, bqnId))
    .then((response) => parseNames(response, "NAME"));

const requestSubscriberGroups = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show api subscriber-group", mode, bqnId))
    .then((response) => parseNames(response, "SUBSCRIBER-GROUP"));

const ControlsAndResultsLayout = styled.div`
  flex: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .input-group {
    margin-bottom: 0;
  }

`;

const allPolicies = { label: "All rate policies", value: "all" };
const allSubscriberGroups = {label: 'All subscriber-groups', value: 'all'};

const FilteringControls = ({
  defaults = {},
  subnet = undefined,
  policy = undefined,
  subscriberGroup = undefined,
  direction = undefined,
  settings,
  setSettings
}) => {
  const [ratePoliciesRequest, setRatePoliciesRequest] = useState(null);
  const [subscriberGroupsRequest, setSubscriberGroupsRequest] = useState(null);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    setRatePoliciesRequest(requestRatePolicies(mode, bqnId));
    setSubscriberGroupsRequest(requestSubscriberGroups(mode, bqnId));
  }, [mode, bqnId])

  const onChange = (event) => {
    const { target } = event;
    const value =
      target.type !== "checkbox"
        ? target.value
        : target.value === "on" && target.checked === true;
    setSettings((settings) => ({
      ...settings,
      [target.name]: value,
    }));
  };
  return (
    <ControlsAndResultsLayout>
      <ColumnsGrid
        columns={4}
        minWidth="15rem"
        gap="3rem"
        rowGap="10px"
        className="subscribers-report-controls"
      >
        <SelectInputWithIcon
          title="Lines"
          name="lines"
          icon="format_line_spacing"
          selected={settings.lines}
          onChange={onChange}
          options={_linesOptions}
        />
        <SelectInputWithIcon
          title="Direction"
          name="direction"
          icon="import_export"
          selected={settings.direction}
          onChange={onChange}
          options={_directionOptions}
        />
        <Request during={ratePoliciesRequest}>
          {({ names }) => (
            <SelectAutocomplete
              title="Policies"
              name="policy"
              icon="folder_open"
              selected={settings.policy}
              onChange={(value) => {
                setSettings({ ...settings, ["policy"]: value });
              }}
              options={[allPolicies, ...names.map(nameAsOption)]}
            />
          )}
        </Request>
        <Request during={subscriberGroupsRequest}>
          {({ names }) => {
            const options = [...names].reduce((acc, value) => {
              if (value !== "all-subscribers") {
                acc.push({ label: value, value });
              }
              return acc;
            }, []);
            return (
              <SelectAutocomplete
                title="Subscriber groups"
                name="subscriberGroup"
                icon="group"
                selected={settings.subscriberGroup}
                onChange={(value) => {
                  setSettings({ ...settings, ["subscriberGroup"]: value });
                }}
                options={[allSubscriberGroups, ...options]}
              />
            );
          }}
        </Request>
        <AddressInput
          value={settings.subnet}
          name="subnet"
          onChange={onChange}
          className="input-group"
        />
      </ColumnsGrid>
    </ControlsAndResultsLayout>
  );
};
export default FilteringControls;
